import axios from 'axios';

// Zugriffe auf die JSON-Endpunkte des Auftrags-Editors. Ein Ort für alle
// Adressen, damit sich der Vertrag mit dem Server an einer Stelle nachlesen
// lässt. Der CSRF-Schutz hängt an der Grundkonfiguration von axios.

/**
 * Referenz des Belegs für alle Endpunkte (DocumentReference im Server).
 *
 * Es gibt zwei Einstiege: die Shop-Bestellung (`order.id`) und der Beleg ohne
 * Shop-Bestellung (`w<weclappOrderId>`, im Handel direkt in weclapp erfasst).
 * Alle Adressen nehmen dieselbe Referenz, deshalb wird sie hier einmal
 * abgeleitet statt an jeder Aufrufstelle.
 *
 * @param {{id: ?number|string, weclappOrderId: string}} order - Prop `order` der Seite.
 */
export function docRef(order) {
    if (order && order.id !== null && order.id !== undefined && order.id !== '') {
        return String(order.id);
    }
    return 'w' + String((order && order.weclappOrderId) || '');
}

const base = (ref) => `/order-editor/${encodeURIComponent(ref)}`;

/** Beleg laden. Ohne `number` liefert der Server den verknüpften Auftrag. */
export async function loadDocument(ref, {type = 'order', number = null} = {}) {
    const {data} = await axios.get(`${base(ref)}/load`, {params: {type, number: number || undefined}});
    return data;
}

/** Bestand, Rabattregeln und Stammdaten zum geladenen Beleg. */
export async function loadStock(ref, {type, id}) {
    const {data} = await axios.get(`${base(ref)}/stock`, {params: {type, id}});
    return data;
}

/** Belegversion für den Frische-Vergleich. */
export async function loadDocMeta(ref, {type, id}) {
    const {data} = await axios.get(`${base(ref)}/docmeta`, {params: {type, id}});
    return data;
}

/** Artikelsuche für die Add-Zeile (Stufe 1). */
export async function searchArticles(q) {
    const {data} = await axios.get('/order-editor/articles', {params: {q}});
    return data;
}

/** Einzelheiten zu einem Artikel (Bestand, Gruppe, Preis) — Stufe 1. */
export async function loadArticleDetail(ref, articleId, qty) {
    const {data} = await axios.get(`${base(ref)}/article/${encodeURIComponent(articleId)}`, {params: {qty}});
    return data;
}

/**
 * Diff-Save anstoßen. Der Server antwortet mit 202 und der Job-Kennung; der
 * Fortschritt läuft danach über `/weclapp/transmission/status/{jobId}`
 * (useTransmissionPolling).
 *
 * @returns {Promise<{jobId: number, status: string, message?: string}>}
 */
export async function saveChanges(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/save`, payload);
    return data;
}

/**
 * Arbeitsstand des Belegs lesen: Prüf-Haken (Stufe 3), Wizard-Marker (Stufe 3)
 * und die Positionen ohne Lieferung (Stufe 1, „Keine").
 *
 * @returns {Promise<{checkedItemIds: string[], wizard: object, itemOverrides: object}>}
 */
export async function loadEditorState(ref, {type, id} = {}) {
    const {data} = await axios.get(`${base(ref)}/state`, {params: {type, id}});
    return data;
}

/** Arbeitsstand schreiben — nur die mitgeschickten Felder ändern sich. */
export async function saveEditorState(ref, patch) {
    const {data} = await axios.put(`${base(ref)}/state`, patch);
    return data;
}

/** Liste der am Beleg hängenden PDF-Dokumente. */
export async function loadDocs(ref, {type, id}) {
    const {data} = await axios.get(`${base(ref)}/docs`, {params: {type, id}});
    return data;
}

/**
 * Adresse eines Beleg-PDFs. Wird als `src` eines iframes und als Ziel des
 * Herunterladens benutzt, deshalb nur die Adresse statt eines Abrufs.
 */
export function docUrl(ref, docId, {type, id, download = false}) {
    const params = new URLSearchParams({type, id: String(id)});
    if (download) {
        params.set('dl', '1');
    }
    return `${base(ref)}/doc/${encodeURIComponent(docId)}?${params.toString()}`;
}

/** USt-ID beim EU-VIES-Dienst prüfen. */
export async function checkVat(vat) {
    const {data} = await axios.get('/order-editor/vat-check', {params: {vat}});
    return data;
}

/** Aktuelle Tabellenansicht als Excel-Datei erzeugen und herunterladen. */
export async function exportView(ref, payload) {
    const response = await axios.post(`${base(ref)}/export`, payload, {responseType: 'blob'});
    return response.data;
}

/**
 * Gegenprobe der Frachtrechnung im Server (`FreightCalculator`). Die
 * Oberfläche rechnet reaktiv selbst; dieser Aufruf prüft das Ergebnis gegen
 * dieselbe Rechnung auf der anderen Seite (ABGLEICH N30).
 *
 * @param {string} ref
 * @param {{iso: string, zip: ?string, weights: object, volumes: object, pallets: object, spray: boolean}} payload
 * @returns {Promise<{ways: object[], cheapest: ?string, mixed: boolean}>}
 */
export async function compareFreight(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/freight/compare`, payload);
    return data;
}

/**
 * Paketrechner des Versand-Assistenten: Kartonaufteilung, Zone, Kundenpreis,
 * Selbstkosten und Versicherungshinweis für die gezeigten Positionen. Liest
 * und rechnet nur; geschrieben wird nichts.
 *
 * @param {string} ref
 * @param {{iso: ?string, country: ?string, channel: ?string, items: object[]}} payload
 * @returns {Promise<object>}
 */
export async function calcParcels(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/parcel/calc`, payload);
    return data;
}

// ── Stufe 3: Lager-Wizard ───────────────────────────────────────────────────
// Die Endpunktnamen sind die des Originals (`/api/komm-wizard`, `/api/pick`,
// …), damit der Abgleich Zeile für Zeile möglich bleibt. Geschützte Aktionen
// (Buchen, Versenden, Festschreiben, Produktion, Abschluss) antworten im
// Testmodus mit `dryRun: true` und einem Klartext in `message`.

/** Kommissionierschein über die weclapp-Checkbox erzeugen (Rückfallebene). */
export async function kommCreate(ref) {
    const {data} = await axios.post(`${base(ref)}/komm-create`, {});
    return data;
}

/** Festschreiben zurücknehmen (zurück auf „Auftragserfassung"). */
export async function orderReopen(ref) {
    const {data} = await axios.post(`${base(ref)}/order-reopen`, {});
    return data;
}

/**
 * Beide Kommissionierscheine des Wizards erzeugen. Antwortet mit 202 und der
 * Job-Kennung; das Ergebnis (`{ok, name, docId, docs[], lahrPos, schneiderPos}`)
 * kommt über `useTransmissionPolling`.
 */
export async function kommWizard(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/komm-wizard`, payload);
    return data;
}

/** Druck-Agenten mit ihren Druckern und dem persönlichen Standarddrucker. */
export async function loadPrinters(ref) {
    const {data} = await axios.get(`${base(ref)}/printers`);
    return data;
}

/** Druckmaske (Ticket #130): weclapp-PDFs am Auftrag, die mitgedruckt werden können. */
export async function loadDruckmappeDocs(ref) {
    const {data} = await axios.get(`${base(ref)}/druckmappe/docs`);
    return data;
}

/** Druckmaske: Vorschau der Mappe als PDF-Blob (speichert nichts). */
export async function previewDruckmappe(ref, payload) {
    const res = await axios.post(`${base(ref)}/druckmappe/preview`, payload, {responseType: 'blob'});
    return {blob: res.data, pages: parseInt(res.headers['x-druckmappe-seiten'] || '0', 10) || null};
}

/** Druckmaske: Mappe an den Auftrag hängen und mit den gewählten Belegen drucken. */
export async function printDruckmappe(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/druckmappe/print`, payload);
    return data;
}

/** Druckauftrag an den weclapp-Druck-Agenten. */
export async function printDocument(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/print`, payload);
    return data;
}

/** Produktionsplan mit Stücklisten-Deckung und bestehenden Aufträgen. */
export async function loadProductionPlan(ref) {
    const {data} = await axios.get(`${base(ref)}/production-plan`);
    return data;
}

/** Produktionsaufträge anlegen (nur anlegen — fertigmelden geht in weclapp). */
export async function createProduction(ref, items) {
    const {data} = await axios.post(`${base(ref)}/production`, {items});
    return data;
}

/** Bestandskorrektur ohne Belegbezug (Wareneingang/-ausgang). */
export async function bookStock(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/stock-book`, payload);
    return data;
}

/** Entnahme/Reservierung an Positionen setzen. */
export async function bookPick(ref, items) {
    const {data} = await axios.post(`${base(ref)}/pick`, {items});
    return data;
}

/** Reservierung an Positionen wieder aufheben. */
export async function unbookPick(ref, itemIds) {
    const {data} = await axios.post(`${base(ref)}/unpick`, {itemIds});
    return data;
}

/**
 * Lieferschein(e) erzeugen. 202 mit Job-Kennung; das Ergebnis
 * (`{ok, shipments[], shortfalls[], xlsx, comment, error}`) kommt über
 * `useTransmissionPolling`.
 */
export async function createShipments(ref, payload) {
    const {data} = await axios.post(`${base(ref)}/shipments`, payload);
    return data;
}

/** Lieferschein stornieren/löschen. */
export async function cancelShipment(ref, shipmentId) {
    const {data} = await axios.post(`${base(ref)}/shipment-cancel`, {shipmentId: String(shipmentId)});
    return data;
}

/**
 * Fehlmengen-Datei neu rechnen und die angehängte ersetzen (Ticket #120),
 * z. B. nachdem ein Lieferschein direkt in weclapp storniert wurde.
 */
export async function refreshShortfall(ref) {
    const {data} = await axios.post(`${base(ref)}/shortfall-refresh`);
    return data;
}

/**
 * Zusatz für die Meldung nach einem Storno: Was ist mit der
 * Fehlmengen-Datei passiert? Leer, wenn es keinen Lieferlauf aus dem Editor
 * gab.
 */
export function shortfallNote(j) {
    const f = j && j.shortfallFile;
    if (!f || f.skipped) {
        return '';
    }
    if (!f.ok) {
        return ` · Fehlmengen-Datei nicht aktualisiert: ${f.error || 'unbekannt'}`;
    }
    return f.shortfalls
        ? ` · Fehlmengen-Datei neu berechnet (${f.shortfalls} Position(en))`
        : ' · Fehlmengen-Datei entfernt, es fehlt nichts mehr';
}

/** Lieferschein versenden — beim Schneider-Beleg die Freigabe an Schneider. */
export async function shipShipment(ref, shipmentId) {
    const {data} = await axios.post(`${base(ref)}/shipment-ship`, {shipmentId: String(shipmentId)});
    return data;
}

/** Schneider-/SNV-Felder eines Lieferscheins lesen (Versandart, Status, IMO). */
export async function loadSnvInfo(ref, shipmentId) {
    const {data} = await axios.get(`${base(ref)}/shipment-snv`, {params: {id: String(shipmentId)}});
    return data;
}

/** Palettenanzahl für die Gefahrgut-Erklärung setzen. */
export async function saveImoPallets(ref, shipmentId, pallets) {
    const {data} = await axios.post(`${base(ref)}/shipment-imo-pallets`, {
        shipmentId: String(shipmentId),
        pallets,
    });
    return data;
}

/** „Beistellung angekommen"-Vermerk lesen. */
export async function loadArrived(ref) {
    const {data} = await axios.get(`${base(ref)}/arrived`);
    return data;
}

/** „Beistellung angekommen" am Auftrag vermerken (Kommentar in weclapp). */
export async function confirmArrived(ref) {
    const {data} = await axios.post(`${base(ref)}/arrived`, {});
    return data;
}

/** Auftrag abschließen — offener Rückstand verfällt. */
export async function closeOrder(ref) {
    const {data} = await axios.post(`${base(ref)}/order-close`, {});
    return data;
}

/** Prüfen, ob der Auftrag löschbar ist oder nur storniert werden kann. */
export async function terminateInfo(ref, id) {
    const {data} = await axios.get(`${base(ref)}/order-terminate-info`, {params: {id: String(id)}});
    return data;
}

/** Auftrag endgültig löschen. */
export async function deleteOrder(ref) {
    const {data} = await axios.post(`${base(ref)}/order-delete`, {});
    return data;
}

/** Auftrag stornieren bzw. manuell schließen. */
export async function cancelOrder(ref) {
    const {data} = await axios.post(`${base(ref)}/order-cancel`, {});
    return data;
}

// ── Automatisches Reservieren im Hintergrund (1.2.15) ──────────────────────

/**
 * Stand des automatischen Reservierens: läuft gerade ein Lauf, und was hat der
 * letzte Lauf je Position ergeben? Rein lesend — aktualisiert die Seite nie.
 */
export async function loadAutoReserve(ref) {
    const {data} = await axios.get(`${base(ref)}/auto-reserve`);
    return data;
}

/** Einen Lauf von Hand anstoßen (Recht „Lager"). 202 mit Job-Kennung. */
export async function startAutoReserve(ref) {
    const {data} = await axios.post(`${base(ref)}/auto-reserve`, {});
    return data;
}

/** Lieferschein festschreiben (erzeugt das Beleg-PDF) — für den früh angelegten SNV-Lieferschein. */
export async function finalizeShipment(ref, shipmentId) {
    const {data} = await axios.post(`${base(ref)}/shipment-finalize`, {shipmentId: String(shipmentId)});
    return data;
}

/** „Paket ab SNV" oder „Palette ab SNV" am SNV-Lieferschein setzen. */
export async function setSnvVersand(ref, shipmentId, kind) {
    const {data} = await axios.post(`${base(ref)}/shipment-snv-versand`, {shipmentId: String(shipmentId), kind});
    return data;
}
